import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Upload,
    Image,
    X,
    Plus,
    Search,
    Video,
    Images,
    Building2,
    FileText,
    Tags,
    Users,
    Check,
    Loader2,
    type LucideIcon,
} from 'lucide-react';
import { SelectTags, type NewTagState } from '../../Tags/SelectTags';
import { deriveTagStateFromSource, flattenTagStateToNames } from '../../Tags/tagStateUtils';
import { userService } from '../../../services/userService';
import { useToast } from '../../UI/Toast';
import type { WorkCollaborator, WorkSubmitMode, WorkVisibility } from '../../../services/postService';
import { isOfficialUserId } from '../../../constants/official';
import { STUDIO_INDUSTRIES, type StudioIndustry } from '../../../constants/studio';

// Keep in sync with FILE_SIZE_LIMITS in
// GenAIPlatformWebAppService/src/services/mediaService.ts.
const PUBLISH_VIDEO_MAX_BYTES = 500 * 1024 * 1024;  // 500MB
const PUBLISH_IMAGE_MAX_BYTES = 50 * 1024 * 1024;   // 50MB
const VIDEO_FRAME_MAX_WIDTH = 1280;

interface VideoFrameCandidate {
    url: string;
    file: File;
    time: number;
}

const seekVideo = (video: HTMLVideoElement, time: number) => new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('视频截帧超时'));
    }, 8000);

    const cleanup = () => {
        window.clearTimeout(timeoutId);
        video.removeEventListener('seeked', handleSeeked);
        video.removeEventListener('error', handleError);
    };
    const handleSeeked = () => {
        cleanup();
        resolve();
    };
    const handleError = () => {
        cleanup();
        reject(new Error('无法读取视频画面'));
    };

    video.addEventListener('seeked', handleSeeked, { once: true });
    video.addEventListener('error', handleError, { once: true });
    video.currentTime = Math.min(Math.max(0, time), Math.max(0, video.duration - 0.05));
});

const canvasToJpegBlob = (canvas: HTMLCanvasElement) => new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob) {
            resolve(blob);
            return;
        }
        reject(new Error('无法生成视频封面'));
    }, 'image/jpeg', 0.86);
});

const extractVideoFrameCandidates = async (sourceUrl: string, sourceName: string): Promise<VideoFrameCandidate[]> => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = sourceUrl;

    await new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
            cleanup();
            reject(new Error('读取视频信息超时'));
        }, 10000);
        const cleanup = () => {
            window.clearTimeout(timeoutId);
            video.removeEventListener('loadedmetadata', handleLoaded);
            video.removeEventListener('error', handleError);
        };
        const handleLoaded = () => {
            cleanup();
            resolve();
        };
        const handleError = () => {
            cleanup();
            reject(new Error('无法读取视频信息'));
        };

        video.addEventListener('loadedmetadata', handleLoaded, { once: true });
        video.addEventListener('error', handleError, { once: true });
        video.load();
    });

    if (!Number.isFinite(video.duration) || video.duration <= 0 || !video.videoWidth || !video.videoHeight) {
        throw new Error('视频信息不完整，无法自动截帧');
    }

    const frameCount = video.duration > 30 ? 10 : 5;
    const scale = Math.min(1, VIDEO_FRAME_MAX_WIDTH / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('当前浏览器无法自动截帧');
    }

    const baseName = sourceName.replace(/\.[^.]+$/, '') || 'video';
    const candidates: VideoFrameCandidate[] = [];
    try {
        for (let index = 0; index < frameCount; index += 1) {
            const time = video.duration * ((index + 0.5) / frameCount);
            await seekVideo(video, time);
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const blob = await canvasToJpegBlob(canvas);
            const file = new File([blob], `${baseName}-cover-${index + 1}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now(),
            });
            candidates.push({
                url: URL.createObjectURL(blob),
                file,
                time,
            });
        }
        return candidates;
    } catch (error) {
        candidates.forEach((candidate) => URL.revokeObjectURL(candidate.url));
        throw error;
    } finally {
        video.removeAttribute('src');
        video.load();
    }
};

interface WorkFormProps {
    initialData?: {
        videoUrl?: string;
        videoPreview?: string;
        videoFile?: File | null;
        thumbnailUrl?: string;
        coverPreview?: string;
        coverFile?: File | null;
        title: string;
        description: string;
        tags: string[];
        categories?: string[];
        collaborators?: WorkCollaborator[];
        workType?: 'video' | 'image';
        imageUrls?: string[];
        imagePreviews?: string[];
        imageFiles?: File[];
        visibility?: WorkVisibility;
        industry?: StudioIndustry;
    };
    mode?: 'create' | 'edit';
    onSubmit: (data: any, submitMode: WorkSubmitMode) => Promise<void>;
    onCancel?: () => void;
    isSubmitting?: boolean;
    submitMode?: WorkSubmitMode | null;
    formId?: string;
    onDirtyChange?: (dirty: boolean) => void;
    onDataChange?: (data: any) => void;
}

interface WorkFormSectionHeaderProps {
    icon: LucideIcon;
    title: string;
    description: string;
    required?: boolean;
    action?: React.ReactNode;
}

const WorkFormSectionHeader: React.FC<WorkFormSectionHeaderProps> = ({
    icon: Icon,
    title,
    description,
    required = false,
    action,
}) => (
    <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <Icon className="h-[19px] w-[19px]" aria-hidden="true" />
            </span>
            <div className="min-w-0">
                <h2 className="text-[15px] font-semibold text-slate-800">
                    {title}
                    {required && <span className="ml-1 text-red-500">*</span>}
                </h2>
                <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
            </div>
        </div>
        {action && <div className="shrink-0 pl-[52px] sm:pl-0">{action}</div>}
    </div>
);

const WorkForm: React.FC<WorkFormProps> = ({
    initialData,
    mode = 'create',
    onSubmit,
    onCancel,
    isSubmitting = false,
    submitMode = null,
    formId = 'work-form',
    onDirtyChange,
    onDataChange
}) => {
    const { showToast } = useToast();
    const videoInputRef = useRef<HTMLInputElement>(null);
    const coverInputRef = useRef<HTMLInputElement>(null);
    const imagesInputRef = useRef<HTMLInputElement>(null);
    const frameExtractionRequestRef = useRef(0);
    const frameCandidatesRef = useRef<VideoFrameCandidate[]>([]);

    // Form state
    const [workType, setWorkType] = useState<'video' | 'image'>(initialData?.workType || 'video');

    // Video State
    const [videoFile, setVideoFile] = useState<File | null>(initialData?.videoFile || null);
    const [videoPreview, setVideoPreview] = useState<string>(initialData?.videoPreview || initialData?.videoUrl || '');
    const [videoFrameCandidates, setVideoFrameCandidates] = useState<VideoFrameCandidate[]>([]);
    const [isExtractingVideoFrames, setIsExtractingVideoFrames] = useState(false);

    // Image Set State
    const [imageFiles, setImageFiles] = useState<File[]>(initialData?.imageFiles || []);
    const [imagePreviews, setImagePreviews] = useState<string[]>(initialData?.imagePreviews || initialData?.imageUrls || []);

    // Common State
    const [coverFile, setCoverFile] = useState<File | null>(initialData?.coverFile || null);
    const [coverPreview, setCoverPreview] = useState<string>(initialData?.coverPreview || initialData?.thumbnailUrl || '');
    const [title, setTitle] = useState(initialData?.title || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [industry, setIndustry] = useState<StudioIndustry | null>(initialData?.industry || null);
    const [publisherProfileStatus, setPublisherProfileStatus] = useState<'loading' | 'official' | 'regular' | 'unknown'>('loading');
    const isOfficialPublisher = publisherProfileStatus === 'official';
    const isPublisherProfileLoading = publisherProfileStatus === 'loading';

    // Layered tags state
    const [tagState, setTagState] = useState<NewTagState>({
        primaryCategory: null,
        secondaryCategories: [],
        toolTags: [],
        styleFacets: [],
    });

    // Initialize tags from initialData
    useEffect(() => {
        setTagState(deriveTagStateFromSource({
            ...initialData,
            primaryCategory: initialData?.categories?.[0] || null,
            secondaryCategories: initialData?.categories?.slice(1, 3) || [],
        }));
    }, [initialData]);

    const [collaboratorOptions, setCollaboratorOptions] = useState<WorkCollaborator[]>([]);
    const [isCollaboratorSearchLoading, setIsCollaboratorSearchLoading] = useState(false);
    const [followingCollaborators, setFollowingCollaborators] = useState<WorkCollaborator[]>([]);
    const [selectedCollaborators, setSelectedCollaborators] = useState<WorkCollaborator[]>(
        initialData?.collaborators || []
    );
    const [collaboratorSearch, setCollaboratorSearch] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        setWorkType(initialData?.workType || 'video');
        setVideoFile(initialData?.videoFile || null);
        setVideoPreview(initialData?.videoPreview || initialData?.videoUrl || '');
        setImageFiles(initialData?.imageFiles || []);
        setImagePreviews(initialData?.imagePreviews || initialData?.imageUrls || []);
        setCoverFile(initialData?.coverFile || null);
        setCoverPreview(initialData?.coverPreview || initialData?.thumbnailUrl || '');
        setTitle(initialData?.title || '');
        setDescription(initialData?.description || '');
        setIndustry(initialData?.industry || null);
        setSelectedCollaborators(initialData?.collaborators || []);
        setCollaboratorSearch('');
        setCollaboratorOptions([]);
    }, [initialData]);

    const replaceVideoFrameCandidates = useCallback((nextCandidates: VideoFrameCandidate[]) => {
        frameCandidatesRef.current.forEach((candidate) => URL.revokeObjectURL(candidate.url));
        frameCandidatesRef.current = nextCandidates;
        setVideoFrameCandidates(nextCandidates);
    }, []);

    const generateVideoFrameCandidates = useCallback(async (
        sourceUrl: string,
        sourceName: string,
        selectFirstFrame: boolean
    ) => {
        const extractionRequestId = frameExtractionRequestRef.current + 1;
        frameExtractionRequestRef.current = extractionRequestId;
        replaceVideoFrameCandidates([]);
        setIsExtractingVideoFrames(true);

        try {
            const candidates = await extractVideoFrameCandidates(sourceUrl, sourceName);
            if (frameExtractionRequestRef.current !== extractionRequestId) {
                candidates.forEach((candidate) => URL.revokeObjectURL(candidate.url));
                return;
            }
            replaceVideoFrameCandidates(candidates);
            if (selectFirstFrame && candidates[0]) {
                setCoverFile(candidates[0].file);
                setCoverPreview(candidates[0].url);
            }
        } catch (error) {
            if (frameExtractionRequestRef.current === extractionRequestId) {
                console.error('Failed to extract video cover frames:', error);
                showToast(error instanceof Error ? error.message : '自动截帧失败，可手动上传封面', 'error');
            }
        } finally {
            if (frameExtractionRequestRef.current === extractionRequestId) {
                setIsExtractingVideoFrames(false);
            }
        }
    }, [replaceVideoFrameCandidates, showToast]);

    useEffect(() => () => {
        frameExtractionRequestRef.current += 1;
        // Keep generated preview URLs alive after the modal closes: the
        // optimistic work card and the in-memory retry snapshot still use
        // the selected frame until the server returns its permanent URL.
    }, []);

    // Video upload handler
    const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (mode === 'edit') return; // Should be disabled anyway
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > PUBLISH_VIDEO_MAX_BYTES) {
                showToast(`视频大小不能超过 500MB（当前 ${(file.size / (1024 * 1024)).toFixed(1)}MB）`, 'error');
                if (videoInputRef.current) videoInputRef.current.value = '';
                return;
            }
            setCoverFile(null);
            setCoverPreview('');
            setVideoFile(file);
            const url = URL.createObjectURL(file);
            setVideoPreview(url);
            await generateVideoFrameCandidates(url, file.name, true);
        }
    };

    // Cover upload handler
    const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > PUBLISH_IMAGE_MAX_BYTES) {
                showToast(`封面图片不能超过 50MB（当前 ${(file.size / (1024 * 1024)).toFixed(1)}MB）`, 'error');
                if (coverInputRef.current) coverInputRef.current.value = '';
                return;
            }
            setCoverFile(file);
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    // Images Set upload handler
    const handleImagesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (mode === 'edit') return;
        const files = Array.from(e.target.files || []);
        const oversize = files.find(f => f.size > PUBLISH_IMAGE_MAX_BYTES);
        if (oversize) {
            showToast(`单张图片不能超过 50MB（${oversize.name} 为 ${(oversize.size / (1024 * 1024)).toFixed(1)}MB）`, 'error');
            if (imagesInputRef.current) imagesInputRef.current.value = '';
            return;
        }
        if (files.length > 0) {
            setImageFiles(prev => [...prev, ...files]);

            const newPreviews = files.map(file => URL.createObjectURL(file));
            setImagePreviews(prev => {
                const updated = [...prev, ...newPreviews];
                // Automatically set cover if not set
                if (!coverPreview && updated.length > 0) {
                    setCoverPreview(updated[0]);
                }
                return updated;
            });
        }
        // clear input value so picking the same files again works
        if (imagesInputRef.current) imagesInputRef.current.value = '';
    };

    const removeImage = (indexToRemove: number) => {
        const removedPreview = imagePreviews[indexToRemove];
        const nextPreviews = imagePreviews.filter((_, index) => index !== indexToRemove);

        setImagePreviews(nextPreviews);
        setImageFiles(prev => prev.filter((_, index) => index !== indexToRemove));

        if (removedPreview === coverPreview) {
            setCoverPreview(nextPreviews[0] || '');
        }
    };



    useEffect(() => {
        let isCancelled = false;

        const loadFollowingCollaborators = async () => {
            try {
                const profileResponse = await userService.getUserProfile({ suppressAuthPrompt: true });
                const currentUserId = profileResponse.data?.userProfile?.userId;
                if (!profileResponse.success || !currentUserId || isCancelled) {
                    if (!isCancelled) setPublisherProfileStatus('unknown');
                    return;
                }

                setPublisherProfileStatus(
                    profileResponse.data?.userProfile?.isOfficial || isOfficialUserId(currentUserId)
                        ? 'official'
                        : 'regular'
                );

                const response = await userService.getFollowing(currentUserId, { suppressAuthPrompt: true });
                if (isCancelled) {
                    return;
                }

                setFollowingCollaborators(
                    (response.following || [])
                        .map((user: any) => {
                            const id = String(user.userId || user.id || '');
                            return {
                                id,
                                name: user.displayName || user.nickname || user.name || user.username || id,
                                avatar: user.avatarUrl || user.avatar || '',
                                username: user.username || user.handle,
                                verified: user.verified
                            };
                        })
                        .filter((user: WorkCollaborator) => Boolean(user.id))
                );
            } catch (error) {
                console.error('Failed to load publisher profile:', error);
                if (!isCancelled) setPublisherProfileStatus('unknown');
            }
        };

        void loadFollowingCollaborators();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        const keyword = collaboratorSearch.trim();
        if (!keyword) {
            setCollaboratorOptions([]);
            setIsCollaboratorSearchLoading(false);
            return;
        }

        let isCancelled = false;
        const timer = window.setTimeout(async () => {
            setIsCollaboratorSearchLoading(true);
            const normalizedKeyword = keyword.toLowerCase();
            const matchesKeyword = (collaborator: WorkCollaborator) => [
                collaborator.name,
                collaborator.username,
                collaborator.id
            ].some(value => String(value || '').toLowerCase().includes(normalizedKeyword));
            const followedMatches = followingCollaborators.filter(matchesKeyword);
            const response = await userService.searchUsers(keyword, 8);
            if (isCancelled) {
                return;
            }

            if (response.success && response.data?.users) {
                const mergedCollaborators = new Map<string, WorkCollaborator>();

                followedMatches.forEach((collaborator) => {
                    mergedCollaborators.set(collaborator.id, collaborator);
                });

                response.data.users.forEach((user) => {
                    const collaborator = {
                        id: user.userId,
                        name: user.displayName || user.username || user.userId,
                        avatar: user.avatarUrl || '',
                        username: user.username,
                        verified: user.verified
                    };
                    if (!mergedCollaborators.has(collaborator.id)) {
                        mergedCollaborators.set(collaborator.id, collaborator);
                    }
                });

                setCollaboratorOptions(Array.from(mergedCollaborators.values()).slice(0, 8));
            } else {
                setCollaboratorOptions(followedMatches.slice(0, 8));
            }

            setIsCollaboratorSearchLoading(false);
        }, 250);

        return () => {
            isCancelled = true;
            window.clearTimeout(timer);
        };
    }, [collaboratorSearch, followingCollaborators]);

    const addCollaborator = (collaborator: WorkCollaborator) => {
        if (!selectedCollaborators.find(c => c.id === collaborator.id)) {
            setSelectedCollaborators(prev => [...prev, collaborator]);
        }
        setCollaboratorSearch('');
        setCollaboratorOptions([]);
    };

    const removeCollaborator = (id: string) => {
        setSelectedCollaborators(prev => prev.filter(c => c.id !== id));
    };

    const filteredCollaborators = collaboratorOptions.filter(c =>
        !selectedCollaborators.find(sc => sc.id === c.id)
    );

    const isMediaReady = mode === 'edit'
        ? true
        : workType === 'video'
            ? !!videoPreview
            : imagePreviews.length > 0;

    const buildSubmitData = useCallback(() => {
        const tags = flattenTagStateToNames(tagState);
        const categories = [tagState.primaryCategory, ...tagState.secondaryCategories].filter(Boolean) as string[];

        return {
            videoFile,
            videoPreview,
            imageFiles,
            imagePreviews,
            coverFile,
            coverPreview,
            coverImageIndex: workType === 'image' ? imagePreviews.findIndex((preview) => preview === coverPreview) : undefined,
            title,
            description,
            tags,
            categories,
            industry: publisherProfileStatus === 'regular' ? null : industry,
            collaborators: selectedCollaborators,
            workType
        };
    }, [
        videoFile,
        videoPreview,
        imageFiles,
        imagePreviews,
        coverFile,
        coverPreview,
        title,
        description,
        tagState,
        industry,
        publisherProfileStatus,
        selectedCollaborators,
        workType
    ]);

    useEffect(() => {
        onDataChange?.(buildSubmitData());
    }, [buildSubmitData, onDataChange]);

    useEffect(() => {
        const initialCollaborators = (initialData?.collaborators || []).map(item => item.id).sort();
        const currentCollaborators = selectedCollaborators.map(item => item.id).sort();

        const initialSnapshot = JSON.stringify({
            workType: initialData?.workType || 'video',
            title: initialData?.title || '',
            description: initialData?.description || '',
            tags: [...(initialData?.tags || [])].sort(),
            industry: initialData?.industry || null,
            collaborators: initialCollaborators,
            videoPreview: initialData?.videoUrl || '',
            imagePreviews: initialData?.imageUrls || [],
            coverPreview: initialData?.thumbnailUrl || '',
            hasVideoFile: false,
            imageFileCount: 0,
            hasCoverFile: false
        });

        const currentTags = [...(buildSubmitData().tags || [])].sort();

        const currentSnapshot = JSON.stringify({
            workType,
            title,
            description,
            tags: currentTags,
            industry: publisherProfileStatus === 'regular' ? null : industry,
            collaborators: currentCollaborators,
            videoPreview,
            imagePreviews,
            coverPreview,
            hasVideoFile: !!videoFile,
            imageFileCount: imageFiles.length,
            hasCoverFile: !!coverFile
        });

        const isDirty = initialSnapshot !== currentSnapshot;
        setHasUnsavedChanges(isDirty);
        onDirtyChange?.(isDirty);
    }, [
        initialData,
        workType,
        title,
        description,
        tagState,
        industry,
        publisherProfileStatus,
        selectedCollaborators,
        videoPreview,
        imagePreviews,
        coverPreview,
        videoFile,
        imageFiles,
        coverFile,
        buildSubmitData,
        onDirtyChange
    ]);

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
        const nextSubmitMode = (submitter?.dataset.submitMode as WorkSubmitMode | undefined) || 'publish';
        onSubmit(buildSubmitData(), nextSubmitMode);
    };

    return (
        <form
            id={formId}
            onSubmit={handleFormSubmit}
            className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.08)]"
        >
            {/* 媒体上传类型切换 & 区域 */}
            <section className="border-b border-slate-100 p-5 sm:p-6">
                <WorkFormSectionHeader
                    icon={Upload}
                    title="媒体内容"
                    description={mode === 'edit' ? '已发布的媒体内容不可修改' : '上传视频或图集，并设置作品封面'}
                    required
                    action={mode === 'create' ? (
                        <div className="flex rounded-xl bg-slate-100 p-1">
                            <button
                                type="button"
                                onClick={() => setWorkType('video')}
                                className={`flex min-h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition-[background-color,color,box-shadow] ${workType === 'video'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <Video className="w-4 h-4" />
                                视频内容
                            </button>
                            <button
                                type="button"
                                onClick={() => setWorkType('image')}
                                className={`flex min-h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition-[background-color,color,box-shadow] ${workType === 'image'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                <Images className="w-4 h-4" />
                                图集作品
                            </button>
                        </div>
                    ) : undefined}
                />

                <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
                <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                <input ref={imagesInputRef} type="file" multiple accept="image/*" onChange={handleImagesUpload} className="hidden" />

                <div className={`grid gap-4 ${workType === 'video' ? 'md:grid-cols-[minmax(0,1fr)_176px] lg:grid-cols-[minmax(0,1fr)_216px]' : 'grid-cols-1'}`}>
                    {/* 上传区域 */}
                    <div className="flex-1">
                        {workType === 'video' ? (
                            videoPreview ? (
                                <div className="relative aspect-video overflow-hidden rounded-xl border border-slate-200 bg-slate-950 shadow-sm">
                                    <video src={videoPreview} controls className="w-full h-full object-contain" />
                                    {mode === 'create' && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                frameExtractionRequestRef.current += 1;
                                                replaceVideoFrameCandidates([]);
                                                setIsExtractingVideoFrames(false);
                                                setVideoFile(null);
                                                setVideoPreview('');
                                                setCoverFile(null);
                                                setCoverPreview('');
                                                if (videoInputRef.current) videoInputRef.current.value = '';
                                            }}
                                            className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors shadow-sm"
                                        >
                                            <X className="w-3.5 h-3.5 text-white" />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div
                                    onClick={() => mode === 'create' && videoInputRef.current?.click()}
                                    className={`flex aspect-video flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/70 ${mode === 'create' ? 'cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50/50' : ''}`}
                                >
                                    <Upload className="w-8 h-8 text-gray-300 mb-3" />
                                    <p className="text-gray-600 text-base font-medium">点击上传视频</p>
                                    <p className="text-gray-400 text-sm mt-1">支持 MP4, MOV, WebM 格式，最大 500MB</p>
                                </div>
                            )
                        ) : (
                            // Image Set UI
                            <div className="min-h-[12rem] rounded-xl border border-dashed border-slate-300 bg-slate-50/70 p-4 sm:min-h-[14rem]">
                                {imagePreviews.length > 0 ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {imagePreviews.map((preview, idx) => (
                                            <div key={idx} className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                                <img src={preview} alt={`upload-${idx}`} className="w-full h-full object-cover" />

                                                {/* Set as Cover indicator */}
                                                {preview === coverPreview && (
                                                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded shadow">
                                                        封面
                                                    </div>
                                                )}

                                                {/* Overlays on hover */}
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {mode === 'create' && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                                                                className="absolute top-2 right-2 w-6 h-6 bg-red-500/90 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>

                                                            {preview !== coverPreview && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); setCoverPreview(preview); }}
                                                                    className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/90 text-gray-800 text-xs px-2 py-1 rounded shadow-sm font-medium hover:bg-white transition-colors whitespace-nowrap"
                                                                >
                                                                    设为封面
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Append more images button */}
                                        {mode === 'create' && (
                                            <div
                                                onClick={() => imagesInputRef.current?.click()}
                                                className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white text-slate-400 transition-colors hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-500"
                                            >
                                                <Plus className="w-6 h-6 mb-1" />
                                                <span className="text-xs font-medium">继续添加</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => mode === 'create' && imagesInputRef.current?.click()}
                                        className={`flex min-h-[10rem] flex-col items-center justify-center text-center sm:min-h-[12rem] ${mode === 'create' ? 'cursor-pointer hover:text-blue-500' : ''}`}
                                    >
                                        <Images className="w-8 h-8 text-gray-300 mb-3" />
                                        <p className="text-gray-600 text-base font-medium">点击上传图集</p>
                                        <p className="mt-1 px-3 text-sm leading-5 text-gray-400">支持 JPG, PNG, WEBP 格式，单张最大 50MB</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* 封面区域 (仅视频模式需要独立封面上传) */}
                    {workType === 'video' && (
                        <div className="flex w-full flex-col md:w-44">
                            <p className="text-sm text-gray-600 mb-2 flex items-center justify-between">
                                <span className="flex items-center gap-1 font-medium"><Image className="w-4 h-4" /> 封面</span>
                                {videoPreview && coverPreview && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => coverInputRef.current?.click()}
                                            className="text-blue-500 hover:text-blue-600 text-xs font-semibold"
                                        >
                                            更换
                                        </button>
                                        {mode === 'create' && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const defaultFrame = videoFrameCandidates[0];
                                                    if (defaultFrame) {
                                                        setCoverFile(defaultFrame.file);
                                                        setCoverPreview(defaultFrame.url);
                                                    }
                                                }}
                                                disabled={videoFrameCandidates.length === 0}
                                                className="border-l border-gray-200 pl-2 text-xs font-semibold text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                                默认
                                            </button>
                                        )}
                                    </div>
                                )}
                            </p>

                            <div className="flex-1 min-h-[120px]">
                                {coverPreview ? (
                                    <div className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                                        <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                                            <button
                                                type="button"
                                                onClick={() => coverInputRef.current?.click()}
                                                className="text-white text-xs bg-white/20 px-2.5 py-1 rounded border border-white/30 hover:bg-white/30 transition-colors"
                                            >
                                                更换封面
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => coverInputRef.current?.click()}
                                        className="flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/70 font-medium transition-colors hover:border-blue-400 hover:bg-blue-50/50"
                                    >
                                        <Plus className="w-6 h-6 text-gray-200" />
                                        <span className="text-gray-400 text-sm mt-1">设置封面</span>
                                    </div>
                                )}
                            </div>
                            {mode === 'create' && videoPreview && (
                                <div className="mt-3">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <p className="text-xs font-medium text-slate-600">选择视频截帧</p>
                                        {isExtractingVideoFrames && (
                                            <span className="flex items-center gap-1 text-[11px] text-slate-400">
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                生成中
                                            </span>
                                        )}
                                    </div>
                                    {videoFrameCandidates.length > 0 ? (
                                        <div className="grid grid-cols-3 gap-1.5" aria-label="视频封面截帧">
                                            {videoFrameCandidates.map((candidate, index) => {
                                                const isSelected = coverPreview === candidate.url;
                                                return (
                                                    <button
                                                        key={`${candidate.time}-${index}`}
                                                        type="button"
                                                        onClick={() => {
                                                            setCoverFile(candidate.file);
                                                            setCoverPreview(candidate.url);
                                                        }}
                                                        aria-pressed={isSelected}
                                                        title={`选择 ${candidate.time.toFixed(1)} 秒画面`}
                                                        className={`group/frame relative aspect-[4/3] overflow-hidden rounded-md border-2 transition-[border-color,box-shadow] ${isSelected
                                                            ? 'border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.16)]'
                                                            : 'border-transparent hover:border-blue-300'
                                                            }`}
                                                    >
                                                        <img src={candidate.url} alt={`${candidate.time.toFixed(1)} 秒截帧`} className="h-full w-full object-cover" />
                                                        <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 py-0.5 text-[9px] leading-none text-white">
                                                            {candidate.time.toFixed(1)}s
                                                        </span>
                                                        {isSelected && (
                                                            <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-white">
                                                                <Check className="h-2.5 w-2.5" />
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : !isExtractingVideoFrames ? (
                                        <p className="text-xs leading-5 text-slate-400">未能生成截帧时，可点击“更换”上传封面。</p>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* 标题和描述 */}
            <section className="border-b border-slate-100 p-5 sm:p-6">
                <WorkFormSectionHeader
                    icon={FileText}
                    title="作品信息"
                    description="用清晰的标题和说明帮助浏览者快速理解作品"
                />
                <div className="space-y-5 pl-0 sm:pl-[52px]">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            标题 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="给作品起个标题"
                            maxLength={50}
                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-4 text-base transition-[border-color,box-shadow,background-color] placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">作品描述</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="描述你的创作过程、灵感、或者是使用的模型参数..."
                            rows={4}
                            maxLength={500}
                            className="min-h-[128px] w-full resize-y rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-base transition-[border-color,box-shadow,background-color] placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                        />
                        <div className="flex justify-end mt-1">
                            <span className="text-xs text-gray-400">{description.length}/500</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* 标签 */}
            <section className="border-b border-slate-100 p-5 sm:p-6">
                <WorkFormSectionHeader
                    icon={Tags}
                    title="作品标签"
                    description="选择业务分类，并补充风格与使用工具"
                />
                <div className="pl-0 sm:pl-[52px]">
                    <SelectTags
                        value={tagState}
                        onChange={(next) => {
                            setTagState(next);
                            setHasUnsavedChanges(true);
                        }}
                        showCollapsed={false}
                    />
                </div>
            </section>

            {isOfficialPublisher && (
                <section className="border-b border-slate-100 p-5 sm:p-6">
                    <WorkFormSectionHeader
                        icon={Building2}
                        title="行业"
                        description="Studio 专属，可选。选择后作品会自动归档到行业案例页"
                    />
                    <div className="pl-0 sm:pl-[52px]">
                        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4" role="radiogroup" aria-label="作品行业">
                                {STUDIO_INDUSTRIES.map((option) => {
                                    const isSelected = industry === option.id;
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            onClick={() => setIndustry(isSelected ? null : option.id)}
                                            className={`group flex min-h-[52px] items-center justify-between rounded-xl border px-4 py-3 text-left transition-[border-color,background-color,box-shadow] duration-200 ${isSelected
                                                ? 'border-blue-400 bg-blue-50/80 shadow-[0_8px_22px_rgba(37,99,235,0.10)]'
                                                : 'border-slate-200 bg-slate-50/60 hover:border-blue-200 hover:bg-blue-50/30'
                                                }`}
                                        >
                                            <span className={`text-sm font-semibold ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                                                {option.label}
                                            </span>
                                            {isSelected && <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />}
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                </section>
            )}

            {/* 关联创作者 */}
            <section className="p-5 sm:p-6">
                <WorkFormSectionHeader
                    icon={Users}
                    title="关联创作者"
                    description="添加共同参与创作的成员，可选"
                />

                <div className="pl-0 sm:pl-[52px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={collaboratorSearch}
                            onChange={(e) => setCollaboratorSearch(e.target.value)}
                            placeholder="搜索并关联其他创作者..."
                            className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50/70 pl-10 pr-4 text-base font-medium transition-[border-color,box-shadow,background-color] placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                        />

                        {collaboratorSearch && (
                            <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl border border-slate-100 bg-white shadow-xl">
                            {isCollaboratorSearchLoading ? (
                                <div className="px-4 py-3 text-sm text-gray-500">搜索中...</div>
                            ) : filteredCollaborators.length > 0 ? (
                                filteredCollaborators.map(c => (
                                    <div
                                        key={c.id}
                                        onClick={() => addCollaborator(c)}
                                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        {c.avatar ? (
                                            <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full object-cover shadow-sm" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                                                {c.name.slice(0, 1).toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-base font-medium text-gray-900 truncate">{c.name}</p>
                                            <p className="text-sm text-gray-500 truncate">@{c.username || c.id} · ID: {c.id}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                    未找到匹配用户，请输入昵称、用户名或用户 ID
                                </div>
                            )}
                            </div>
                        )}
                    </div>
                </div>

                {selectedCollaborators.length > 0 && (
                    <div className="ml-0 mt-4 flex flex-wrap gap-2.5 sm:ml-[52px]">
                        {selectedCollaborators.map(c => (
                            <div key={c.id} className="flex items-center gap-2 pl-1 pr-3 py-1 bg-gray-100 rounded-full group hover:bg-gray-200 transition-colors">
                                <img src={c.avatar} alt={c.name} className="w-7 h-7 rounded-full object-cover border border-white shadow-sm" />
                                <span className="text-sm font-medium text-gray-700">{c.name}</span>
                                <button type="button" onClick={() => removeCollaborator(c.id)} className="w-5 h-5 bg-gray-300 group-hover:bg-gray-400 rounded-full flex items-center justify-center transition-colors">
                                    <X className="w-3 h-3 text-white" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* 提交按钮区域 (如果是 Modal 模式) */}
            {onCancel && (
                <div className="flex flex-wrap justify-end gap-3 rounded-b-2xl border-t border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="min-h-11 rounded-xl border border-slate-200 bg-white px-6 font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50"
                    >
                        取消
                    </button>
                    <button
                        type="submit"
                        data-submit-mode="draft"
                        disabled={isSubmitting || isPublisherProfileLoading || !title || !isMediaReady || !hasUnsavedChanges}
                        className={`min-h-11 rounded-xl px-7 font-medium transition-[background-color,color] ${isSubmitting || isPublisherProfileLoading || !title || !isMediaReady || !hasUnsavedChanges
                            ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            }`}
                    >
                        {submitMode === 'draft' ? '保存中...' : '保存'}
                    </button>
                    <button
                        type="submit"
                        data-submit-mode="publish"
                        disabled={isSubmitting || isPublisherProfileLoading || !title || !isMediaReady}
                        className={`min-h-11 rounded-xl px-8 font-medium transition-[background-color,color,box-shadow] ${isSubmitting || isPublisherProfileLoading || !title || !isMediaReady
                            ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                            : 'bg-blue-600 text-white shadow-[0_10px_24px_rgba(37,99,235,0.22)] hover:bg-blue-700'
                            }`}
                    >
                        {submitMode === 'publish' ? '发布中...' : '发布'}
                    </button>
                </div>
            )}
        </form>
    );
};

export default WorkForm;
